import { AUTO_GRANTED_SKILL_IDS, isLockedSkill, addStaticSkills } from './utils';
import { updateCalculatedFields } from './characterCalculations';

function positiveInteger(value, field) {
    if ((typeof value !== 'number' && !(typeof value === 'string' && /^\d+$/.test(value))) ||
        !Number.isSafeInteger(Number(value)) || Number(value) <= 0) {
        throw new Error(`Invalid ${field}.`);
    }
    return Number(value);
}

const skillsetId = value => value == null || value === '' ? null : positiveInteger(value, 'skillset');

// An empty catalog is ready and valid; an absent catalog is not. Unknown
// purchases survive serialization so the server can report them, never prune them.
export function normalizeSkillCatalog(catalog) {
    if (!Array.isArray(catalog)) throw new Error('The skill catalog is not ready.');
    return catalog.map(skill => ({
        ...skill, skillId: positiveInteger(skill.skillId, 'catalog skill ID'),
        skillset: skill.skillset == null ? null : Number(skill.skillset),
        tier: skill.tier == null ? null : Number(skill.tier),
    }));
}

export function serializeCharacterBuild(character, catalog) {
    const byId = new Map(normalizeSkillCatalog(catalog).map(skill => [skill.skillId, skill]));
    if (!character || !Array.isArray(character.skills)) throw new Error('Invalid character build.');
    const seen = new Set();
    const skills = character.skills.map(skill => {
        const skillId = positiveInteger(skill.skillId, 'skill ID');
        const ranks = positiveInteger(skill.ranks, 'skill ranks');
        if (seen.has(skillId)) throw new Error('Duplicate skill in character build.');
        seen.add(skillId);
        return { skillId, ranks };
    }).filter(skill => !AUTO_GRANTED_SKILL_IDS.includes(skill.skillId) && !isLockedSkill(byId.get(skill.skillId)))
        .sort((a, b) => a.skillId - b.skillId);
    return {
        skillsetPrimary: skillsetId(character.skillsetPrimary),
        skillsetSecondary: skillsetId(character.skillsetSecondary),
        skills,
    };
}

export const characterBuildSignature = (character, catalog) => JSON.stringify(serializeCharacterBuild(character, catalog));
export const characterBuildsEquivalent = (a, b, catalog) =>
    characterBuildSignature(a, catalog) === characterBuildSignature(b, catalog);

// Call only after server validation with a ready catalog for this character and
// transform. The Phase 5 coordinator owns prompting and request freshness.
export function applyCharacterBuild(character, build, catalog) {
    const normalizedCatalog = normalizeSkillCatalog(catalog);
    const snapshot = serializeCharacterBuild(build, normalizedCatalog);
    const byId = new Map(normalizedCatalog.map(skill => [skill.skillId, skill]));
    const skills = snapshot.skills.map(skill => {
        const base = byId.get(skill.skillId);
        return base ? { ...skill, name: base.name, skillset: base.skillset, prereqs: base.prereqs || [] } : skill;
    });
    return updateCalculatedFields({
        ...character,
        ...snapshot,
        skills: addStaticSkills(skills, normalizedCatalog),
    }, normalizedCatalog);
}
